import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import {
  SearchIcon,
  RefreshIcon,
  UsersIcon,
  SendIcon,
  CancelIcon,
} from "../components/icons";

// Compact star renderer (read-only).
const Stars = ({ value, size = "h-4 w-4" }) => {
  const safe = Math.max(0, Math.min(5, Math.round(value || 0)));
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          className={`${size} ${
            i <= safe
              ? "text-yellow-500 fill-yellow-500"
              : "text-[var(--color-text-muted)] fill-none"
          }`}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
};

// Bureau directory — anonymous-browsable. Requesting association requires
// login as a profile seeker. Each card shows avg rating + review count
// pulled from the enriched /public/bureaus aggregation.
const Bureaus = () => {
  const navigate = useNavigate();
  const [bureaus, setBureaus] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reviewsOpen, setReviewsOpen] = useState(null); // bureau being viewed
  const [reviews, setReviews] = useState(null); // {bureau, summary, data}
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [requestFor, setRequestFor] = useState(null); // bureau for request modal
  const [requestNote, setRequestNote] = useState("");
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  // Map<bureauId, "pending" | "approved"> — drives per-card button state
  // so the seeker can see at a glance which bureau they're already
  // associated with or have a pending request to. Only fetched when the
  // viewer is signed in.
  const [associationByBureau, setAssociationByBureau] = useState({});
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  const isLoggedIn = !!localStorage.getItem("publicUserToken");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setGlobalLoadingMessage("Loading bureaus…");
      setGlobalLoading(true);
      const qs = search.trim()
        ? `?search=${encodeURIComponent(search.trim())}&limit=100`
        : "?limit=100";
      const res = await apiFetch(`/public/bureaus${qs}`);
      setBureaus(res?.data || []);
    } catch (err) {
      setError(err.message || "Failed to load bureaus");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  // Fetch the seeker's associations once on mount (and after any
  // successful submit) so each bureau card can reflect the relationship.
  const loadAssociations = useCallback(async () => {
    if (!isLoggedIn) {
      setAssociationByBureau({});
      return;
    }
    try {
      const res = await apiFetch("/public/bureau-associations/me");
      const map = {};
      (res?.data || []).forEach((it) => {
        const bid = it.bureau?._id || it.bureau;
        if (!bid) return;
        // Only "active" relationships block re-request; rejected and
        // ended states explicitly allow a fresh request.
        if (it.status === "pending" || it.status === "approved") {
          map[bid.toString()] = it.status;
        }
      });
      setAssociationByBureau(map);
    } catch (_) {
      /* non-fatal — fall back to no badges */
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadAssociations();
  }, [loadAssociations]);

  const openReviews = async (bureau) => {
    setReviewsOpen(bureau);
    setReviews(null);
    setReviewsLoading(true);
    try {
      const res = await apiFetch(
        `/public/bureaus/${bureau._id}/reviews?limit=50`
      );
      setReviews(res);
    } catch (err) {
      setReviews({ data: [], summary: { avgRating: null, count: 0 } });
    } finally {
      setReviewsLoading(false);
    }
  };

  const openRequest = (bureau) => {
    if (!isLoggedIn) {
      // Redirect to login; after login the seeker can come back and click.
      navigate("/login");
      return;
    }
    setRequestFor(bureau);
    setRequestNote("");
    setRequestError("");
    setRequestSent(false);
  };

  const submitRequest = async () => {
    if (!requestFor) return;
    setRequestBusy(true);
    setRequestError("");
    try {
      await apiFetch("/public/bureau-associations", {
        method: "POST",
        body: JSON.stringify({
          bureauId: requestFor._id,
          message: requestNote || undefined,
        }),
      });
      setRequestSent(true);
      // Refresh so the bureau card switches to "Pending" immediately.
      loadAssociations();
    } catch (err) {
      setRequestError(err.message || "Failed to send request");
    } finally {
      setRequestBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <UsersIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                Bureaus
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                Browse marriage bureaus, read reviews, request association
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
          >
            <RefreshIcon className="w-4 h-4" />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="flex gap-2"
        >
          <div className="relative flex-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-[var(--color-text-muted)]">
              <SearchIcon className="w-4 h-4" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, owner, address"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-2 pl-11 pr-3 text-sm"
              autoCapitalize="off"
              autoCorrect="off"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-4 py-2 text-sm font-semibold transition-colors"
          >
            Search
          </button>
        </form>

        {error && (
          <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
            {error}
          </div>
        )}

        {!loading && bureaus.length === 0 && !error && (
          <div className="text-center py-12 text-sm text-[var(--color-text-muted)]">
            No bureaus found.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bureaus.map((b) => (
            <div
              key={b._id}
              className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-2"
            >
              <h3 className="text-base font-semibold text-[var(--color-text-strong)] truncate">
                {b.name}
              </h3>
              {b.ownerName && (
                <div className="text-xs text-[var(--color-text-muted)] truncate">
                  Owner: {b.ownerName}
                </div>
              )}
              {b.address && (
                <div className="text-xs text-[var(--color-text-muted)] truncate">
                  {b.address}
                </div>
              )}

              <div className="flex items-center gap-2 mt-1">
                <Stars value={b.avgRating || 0} />
                <span className="text-xs text-[var(--color-text)]">
                  {b.avgRating ? b.avgRating.toFixed(1) : "—"}
                  <span className="text-[var(--color-text-muted)]">
                    {" "}
                    ({b.reviewCount || 0})
                  </span>
                </span>
              </div>

              <div className="text-xs text-[var(--color-text-muted)]">
                {b.profileCount || 0} active{" "}
                {b.profileCount === 1 ? "profile" : "profiles"}
              </div>

              <div className="flex gap-2 flex-wrap mt-auto pt-2">
                <button
                  type="button"
                  onClick={() => openReviews(b)}
                  className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] px-3 py-1.5 text-xs font-medium transition-colors"
                >
                  View reviews
                </button>
                {(() => {
                  // Per-card action varies by the seeker's existing
                  // relationship with THIS bureau:
                  //   - approved → "Your bureau" link to /my-bureau
                  //   - pending  → disabled "Pending" pill
                  //   - none     → primary "Request association" button
                  // For anonymous viewers we always show the primary
                  // button (clicking redirects to login).
                  const rel = associationByBureau[b._id?.toString()];
                  if (rel === "approved") {
                    return (
                      <button
                        type="button"
                        onClick={() => navigate("/my-bureau")}
                        className="inline-flex items-center gap-1 rounded-lg border-2 border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary-soft)] px-3 py-1.5 text-xs font-semibold transition-colors"
                      >
                        ✓ Your bureau
                      </button>
                    );
                  }
                  if (rel === "pending") {
                    return (
                      <button
                        type="button"
                        disabled
                        className="inline-flex items-center gap-1 rounded-lg border border-yellow-400 bg-yellow-50 text-yellow-800 px-3 py-1.5 text-xs font-semibold cursor-not-allowed"
                      >
                        Request pending
                      </button>
                    );
                  }
                  return (
                    <button
                      type="button"
                      onClick={() => openRequest(b)}
                      className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-3 py-1.5 text-xs font-semibold transition-colors"
                    >
                      <SendIcon className="w-3 h-3" />
                      Request association
                    </button>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reviews modal */}
      {reviewsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden rounded-xl bg-[var(--color-surface)] shadow-lg">
            <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)] flex-shrink-0">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-[var(--color-text-strong)] truncate">
                  {reviewsOpen.name} — reviews
                </h3>
                {reviews?.summary && (
                  <div className="mt-1 flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
                    <Stars value={reviews.summary.avgRating || 0} />
                    <span>
                      {reviews.summary.avgRating
                        ? reviews.summary.avgRating.toFixed(1)
                        : "—"}{" "}
                      from {reviews.summary.count} review
                      {reviews.summary.count === 1 ? "" : "s"}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setReviewsOpen(null)}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {reviewsLoading && (
                <div className="text-sm text-[var(--color-text-muted)]">
                  Loading…
                </div>
              )}
              {!reviewsLoading && reviews?.data?.length === 0 && (
                <div className="text-sm text-[var(--color-text-muted)] text-center py-8">
                  No reviews yet. Be the first to share your experience.
                </div>
              )}
              {!reviewsLoading && reviews?.data?.length > 0 && (
                <ul className="space-y-3">
                  {reviews.data.map((r) => (
                    <li
                      key={r._id}
                      className="border border-[var(--color-border)] rounded-lg p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm text-[var(--color-text-strong)]">
                          {r.reviewerName}
                          {r.verifiedClient && (
                            <span className="ml-2 inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 font-semibold uppercase tracking-wider">
                              Verified client
                            </span>
                          )}
                        </div>
                        <Stars value={r.rating} size="h-3.5 w-3.5" />
                      </div>
                      {r.comment && (
                        <p className="mt-2 text-sm text-[var(--color-text)] whitespace-pre-wrap">
                          {r.comment}
                        </p>
                      )}
                      <div className="mt-2 text-[10px] text-[var(--color-text-muted)]">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Request-association modal */}
      {requestFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-md bg-[var(--color-surface)] rounded-xl shadow-lg p-5">
            <h3 className="text-lg font-semibold text-[var(--color-text-strong)] mb-1">
              Request to associate with {requestFor.name}
            </h3>
            {requestSent ? (
              <>
                <p className="text-sm text-[var(--color-text)] mb-3">
                  Request sent. You'll be notified once the bureau reviews
                  it. Track status under{" "}
                  <button
                    className="font-semibold underline text-[var(--color-primary)]"
                    onClick={() => navigate("/my-bureau")}
                  >
                    My Bureau
                  </button>
                  .
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setRequestFor(null)}
                    className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-3 py-1.5 text-sm font-semibold transition-colors"
                  >
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-[var(--color-text-muted)] mb-3">
                  Send an optional note. The bureau will review your request
                  and decide whether to onboard you as a client.
                </p>
                <textarea
                  value={requestNote}
                  onChange={(e) => setRequestNote(e.target.value)}
                  rows={3}
                  placeholder="Optional message"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
                {requestError && (
                  <div className="mt-2 text-xs text-[var(--color-danger-text)]">
                    {requestError}
                  </div>
                )}
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    onClick={() => setRequestFor(null)}
                    className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] px-3 py-1.5 text-sm font-medium transition-colors"
                  >
                    <CancelIcon className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={submitRequest}
                    disabled={requestBusy}
                    className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
                  >
                    <SendIcon className="w-4 h-4" />
                    {requestBusy ? "Sending…" : "Send request"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Bureaus;
